import axios from 'axios';

import { Constants } from '@/constants';

export enum AccountType {
  BASIC = 0,
  SUPPORTER = 1,
  BUSINESS = 2,
  PARTNER = 3,
  ADMIN = 4,
}

export interface AccountTypeOption {
  value: AccountType;
  label: string;
}

export const ACCOUNT_TYPE_OPTIONS: AccountTypeOption[] = [
  { value: AccountType.BASIC, label: 'Basic' },
  { value: AccountType.SUPPORTER, label: 'Supporter' },
  { value: AccountType.BUSINESS, label: 'Business' },
  { value: AccountType.PARTNER, label: 'Partner' },
  { value: AccountType.ADMIN, label: 'Admin' },
];

export const normalizeAccountType = (value: unknown): AccountType => {
  const numericValue = Number(value);
  const option = ACCOUNT_TYPE_OPTIONS.find(
    (accountType) => accountType.value === numericValue
  );
  return option?.value ?? AccountType.BASIC;
};

export interface BusinessLink {
  id: number;
  name: string;
  hash_id?: string | null;
}

export interface UserBusinessClaim {
  id: number;
  brand_id?: number | null;
  venue_id?: number | null;
  approved?: boolean;
  status?: string | null;
  brand?: BusinessLink | null;
  venue?: BusinessLink | null;
}

export interface ManagedUserDetails {
  id: number;
  user_id: number;
  hash_id?: string | null;
  firebase_id?: string | null;
  first_name: string;
  last_name: string;
  display_name?: string | null;
  full_name?: string | null;
  email: string;
  phone?: string | null;
  active: boolean;
  private_profile: boolean;
  post_public: boolean;
  acct_type: AccountType;
  account_type?: string | null;
  claims?: UserBusinessClaim[];
}

export interface ManagedUserInput {
  first_name: string;
  last_name: string;
  display_name?: string;
  email: string;
  phone?: string;
  active: boolean;
  private_profile: boolean;
  post_public: boolean;
  acct_type: AccountType;
  brand_ids: number[];
  venue_ids: number[];
  send_password_reset?: boolean;
}

export interface UserSaveResponse {
  user: ManagedUserDetails;
  password_reset_sent?: boolean;
}

export const getManagedUser = async (userId: number) =>
  (await axios.get<ManagedUserDetails>(`${Constants.apiPath}/users/${userId}`))
    .data;

export const createManagedUser = async (user: ManagedUserInput) =>
  (await axios.post<UserSaveResponse>(`${Constants.apiPath}/users`, user)).data;

export const updateManagedUser = async (
  userId: number,
  user: ManagedUserInput
) =>
  (
    await axios.put<UserSaveResponse>(
      `${Constants.apiPath}/users/${userId}`,
      user
    )
  ).data;

export const sendManagedUserPasswordReset = async (userId: number) =>
  (
    await axios.post<{ password_reset_sent: boolean }>(
      `${Constants.apiPath}/users/${userId}/password-reset`
    )
  ).data;

const searchBusinesses = async (
  lookupPath: string,
  legacyPath: string,
  query: string
) => {
  const params = { q: query, limit: 30 };
  try {
    return (
      await axios.get<BusinessLink[]>(`${Constants.apiPath}${lookupPath}`, {
        params,
      })
    ).data;
  } catch (error) {
    if (!axios.isAxiosError(error) || error.response?.status !== 404) {
      throw error;
    }
    return (
      await axios.get<BusinessLink[]>(`${Constants.apiPath}${legacyPath}`, {
        params,
      })
    ).data;
  }
};

export const searchBrands = (query: string) =>
  searchBusinesses('/users/lookups/brands', '/brands', query);

export const searchVenues = (query: string) =>
  searchBusinesses('/users/lookups/venues', '/venues', query);
